import { type Component, createSignal, For, Show } from 'solid-js'
import { clickOutside } from '../../shared/directives/click-outside'
import { useUser } from '../../shared/providers/user-provider'
import { Icon } from '@iconify-icon/solid'
import type { OrganizationMembership } from '../../features/auth/domain/models/me-api'

const OrganizationSelector: Component = () => {
  const { currentOrganization, setCurrentOrganization, organizationMemberships } = useUser()
  const [isOpen, setIsOpen] = createSignal(false)

  const toggleOpen = (): void => {
    setIsOpen(!isOpen())
  }

  const selectOrganization = (org: OrganizationMembership): void => {
    setCurrentOrganization(org)
    setIsOpen(false)
  }

  const isSelected = (org: OrganizationMembership): boolean => {
    return currentOrganization()?.organization_id === org.organization_id
  }

  const sortedOrganizations = (): OrganizationMembership[] => {
    return [...organizationMemberships()].sort((a, b) => a.organization_name.localeCompare(b.organization_name))
  }

  return (
    <div
      class="relative w-full"
      ref={(el) => {
        clickOutside(el, () => setIsOpen(false))
      }}
    >
      <button
        onClick={toggleOpen}
        type="button"
        class="flex w-full items-center justify-between gap-2 rounded-md px-0 py-1.5 text-left text-sm font-medium leading-5 text-gray-500 transition-colors hover:text-gray-700"
        aria-haspopup="listbox"
        aria-expanded={isOpen()}
      >
        <Show when={currentOrganization()} fallback={<span class="text-gray-400">Loading...</span>}>
          <span class="truncate">{currentOrganization()?.organization_name}</span>
        </Show>
        <Icon icon="heroicons:chevron-down-20-solid" class="text-[1.25rem]" />
      </button>
      <Show when={isOpen()}>
        <ul
          class="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          role="listbox"
        >
          <For each={sortedOrganizations()}>
            {(org) => (
              <li
                onClick={() => selectOrganization(org)}
                class="relative flex cursor-pointer select-none items-center gap-2 px-3 py-3 text-sm text-gray-700 hover:bg-gray-100"
                classList={{
                  'bg-gray-100': isSelected(org)
                }}
                role="option"
                aria-selected={isSelected(org)}
              >
                <span class="truncate">{org.organization_name}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  )
}

export default OrganizationSelector
